import { GetStaticPaths, GetStaticPathsResult, GetStaticPropsContext } from 'next';

import { getAllPagePaths, getPageSettings } from '../server/services/Pages';
import { getPagePaths } from '../server/services/Sitemap';
import { withPageStaticProps } from 'utils/header';
import PageBuilder, { PageBuilderProps } from 'components/sanity/PageBuilder';
import { withSanityPageProps } from 'components/sanity/withSanityPageProps';

/**
 * Filters the paths returned by Sanity to exclude any that match one of our static
 * page paths
 *
 * This gives Next pages precedence over Sanity pages if there's ever a conflict (i.e.
 * the same path appears in both places)
 */
const getPathsWithoutStaticPages = async (sanityPaths: string[]) => {
  const staticPaths = await getPagePaths();
  const pageSettings = await getPageSettings();

  const { homepage } = pageSettings;

  const pathsWithoutHomepage = sanityPaths.filter((slug) => slug !== homepage.slug?.current);

  const pathsWithLeadingSlash = pathsWithoutHomepage.map((path) => {
    if (path.startsWith('/')) {
      return path;
    }

    return `/${path}`;
  });

  return pathsWithLeadingSlash.filter((slug) => {
    const hasMatchingStaticPage = staticPaths.some((page) => page.path === slug);

    return hasMatchingStaticPage === false;
  });
};

export const getStaticPaths: GetStaticPaths = async (): Promise<GetStaticPathsResult> => {
  const sanityPaths = await getAllPagePaths();

  return {
    paths: await getPathsWithoutStaticPages(sanityPaths),
    fallback: false,
  };
};

const getSanityPageProps = withSanityPageProps();

export const getStaticProps = withPageStaticProps(async (context: GetStaticPropsContext) => {
  try {
    return await getSanityPageProps(context);
  } catch (e) {
    console.error(e);

    return {
      notFound: true,
    };
  }
});

const Page = (props: PageBuilderProps) => <PageBuilder {...props} />;

export default Page;
